import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useStore } from 'store/StoreContext';
import { FormErrors } from 'validators/PasswordValidator';
import { UpdateUserPasswordReqData, callUpdateUserPasswordApi } from 'requests/RequestUser';
import { Passwords } from 'components/templates/PasswordEditTemplate';
import { RoutePath } from 'consts/RoutePath';
import { ApiError } from 'requests/RequestBase';
import { HttpStatus } from 'consts/HttpStatus';
import { ReactError } from 'consts/ReactError';
import { CONFIRM_ERROR_PASSWORD, HARD_TO_GUESS_PASSWORD } from 'utils/messages';

/**
 * ユーザパスワード更新処理のカスタムフック
 * フォームバリデーション -> 更新実処理 -> ページ遷移 を段階的に分けている
 * useStateが非同期なのでuseEffectを使って段階的にしないとエラーをキャッチすることができないため
 */
export const useUpdatePassword = (
	formErrors: FormErrors,
	setFormErrors: React.Dispatch<React.SetStateAction<FormErrors>>,
	getErrorMessage: Function,
	actionError: string,
	setActionError: React.Dispatch<React.SetStateAction<string>>,
	formData: Passwords,
	handleDialogOpen: Function,
	setProcessLoading: React.Dispatch<React.SetStateAction<boolean>>,
) => {
	const [store] = useStore();
	const history = useHistory();

	// フォームバリデーション
	const [isFormValidationFinished, setIsFormValidationFinished] = useState(false);
	const update = async () => {
		setProcessLoading(true);

		Object.keys(formErrors).forEach((k) => {
			setFormErrors((prev) => ({ ...prev, [k]: getErrorMessage(k, formData[k]) }));
		});
		const isPasswordsMatched = formData.newPassword === formData.newPasswordConfirm;
		const hardToGuessPassword = store.loginUserData.gid.toLowerCase() !== formData.newPassword.toLowerCase();
		if (!isPasswordsMatched) {
			setActionError(CONFIRM_ERROR_PASSWORD);
			setIsUpdateFinished(true);
			return;
		}
		// GIDと同じパスワードを設定できない
		if (!hardToGuessPassword) {
			setActionError(HARD_TO_GUESS_PASSWORD);
			setIsUpdateFinished(true);
			return;
		}
		setActionError('');
		setIsFormValidationFinished(true);
	};

	// 更新実処理
	const [isUpdateFinished, setIsUpdateFinished] = useState(false);
	useEffect(() => {
		if (!isFormValidationFinished) {
			return;
		}

		const isError = !!Object.values(formErrors).find((e) => e !== '');
		if (isError) {
			setIsFormValidationFinished(false);
			setProcessLoading(false);
			return;
		}

		const executeUpdate = async () => {
			const requestData: UpdateUserPasswordReqData = {
				gidpk: store.loginUserData.gidpk,
				old_pw: formData.oldPassword,
				new_pw: formData.newPassword,
			};
			const result = await callUpdateUserPasswordApi(requestData);
			if (result.status === HttpStatus.INTERNAL_SERVER_ERROR) {
				setActionError(ReactError.E9999);
				setProcessLoading(false);
				history.push(RoutePath.ERROR);
				return;
			} else if (result.status === HttpStatus.GATEWAY_TIMEOUT) {
				setActionError(ReactError.E9998);
				setProcessLoading(false);
			} else if (result.status !== HttpStatus.OK) {
				const error = result.data as ApiError;
				setActionError(error.message);
				setProcessLoading(false);
			}
			setIsUpdateFinished(true);
		};
		executeUpdate();
	}, [isFormValidationFinished]);

	// 完了ダイアログ表示
	useEffect(() => {
		if (!isUpdateFinished) {
			return;
		}

		const isError = actionError !== '';
		if (isError) {
			setIsFormValidationFinished(false);
			setIsUpdateFinished(false);
			setProcessLoading(false);
			return;
		}

		handleDialogOpen();
	}, [isUpdateFinished]);

	return {
		update,
	};
};
